import type { AppState } from '../core/types';
import type { ClassificationConfig, LabelTarget, LabelTargetSource } from '../presentation/config';
import { masterClassificationAssignments } from './classification';
import { deriveDisplaySet } from './displaySet';
import { selectedFragmentIds } from './selection';
import { replaceSelectionSet, selectedFragmentIdsForSet } from './selectionSet';
import { themeTargetIds } from './themeTarget';

const withinDisplay = (ids: ReadonlySet<string>, display: ReadonlySet<string>): ReadonlySet<string> =>
  new Set([...ids].filter((id) => display.has(id)));

export function reconcileLabelTargetCategories(target: LabelTarget, classification: ClassificationConfig): LabelTarget {
  if (target.source !== 'theme-categories') return target;
  const available = new Set(classification.categories.map(({ value }) => value));
  const sameScheme = !target.referenceId || target.referenceId === classification.id;
  return {
    ...target,
    referenceId: classification.id,
    categoryValues: sameScheme ? (target.categoryValues ?? []).filter((value) => available.has(value)) : [],
  };
}

export function labelTargetFragmentIds(state: AppState, target = state.labelConfig.labelTarget): ReadonlySet<string> {
  const model = state.model;
  if (!model || target.source === 'none') return new Set();
  const display = deriveDisplaySet(state).masterFragmentIds;
  if (target.source === 'scope') return display;
  if (target.source === 'theme' || target.source === 'theme-valued' || target.source === 'theme-categories') {
    const layer = state.layers.find(({ id }) => id === state.classificationConfig.layerId);
    if (layer?.kind !== 'master') return new Set();
    const themeIds = withinDisplay(themeTargetIds(layer, state.classificationConfig.themeTarget), display);
    if (target.source === 'theme') return themeIds;
    if (!state.classificationConfig.field) return new Set();
    const assignments = masterClassificationAssignments(state.model, state.classificationConfig.field, state.classificationConfig.level, themeIds, state.operationalFields, state.classificationConfig);
    if (target.source === 'theme-valued') return new Set([...themeIds].filter((id) => assignments.hasValueByFragmentId.get(id) === true));
    const reconciled = reconcileLabelTargetCategories(target, state.classificationConfig);
    const selected = new Set(reconciled.categoryValues ?? []);
    return new Set([...themeIds].filter((id) => selected.has(assignments.categoryByFragmentId.get(id) ?? '')));
  }
  if (target.source === 'selection') {
    const selected = selectedFragmentIdsForSet(model, state.selectionSet);
    return withinDisplay(selected.size ? selected : selectedFragmentIds(model, state.selection), display);
  }
  if (target.source === 'saved-selection') {
    const saved = state.savedSelections.find(({ id, layerId }) => id === target.referenceId && layerId === model.id)
      ?? [...state.savedSelections].reverse().find(({ layerId }) => layerId === model.id);
    return withinDisplay(selectedFragmentIdsForSet(model, replaceSelectionSet(saved?.entries ?? [])), display);
  }
  return withinDisplay(new Set(target.featureIds), display);
}

export function captureLabelTarget(state: AppState, source: LabelTargetSource, referenceId?: string): LabelTarget {
  const scope = state.workScope;
  const display = deriveDisplaySet(state).masterFragmentIds;
  const selectedSetIds = state.model ? selectedFragmentIdsForSet(state.model, state.selectionSet) : new Set<string>();
  const customIds = source === 'custom' && state.model
    ? withinDisplay(
      selectedSetIds.size ? selectedSetIds : selectedFragmentIds(state.model, state.selection),
      display,
    )
    : new Set<string>();
  const thematic = source === 'theme-categories'
    ? { referenceId: state.classificationConfig.id, categoryValues: state.classificationConfig.categories.map(({ value }) => value) }
    : {};
  const draft: LabelTarget = { source, scope, featureIds: [], ...(referenceId ? { referenceId } : {}), ...thematic };
  return { ...draft, featureIds: source === 'custom' ? [...customIds] : [...labelTargetFragmentIds(state, draft)] };
}

export function hasMasterThemeTarget(state: AppState): boolean {
  return Boolean(state.model && state.classificationConfig.layerId === state.model.id
    && (state.classificationConfig.mode === 'single' || state.classificationConfig.field)
    && state.classificationConfig.themeTarget.featureIds.length);
}
